// Two-note chord space (Tymoczko): unordered dyads live on a Möbius strip.
// Fundamental domain: u = the bottom note's pitch class, d = the interval
// (0..6). u wraps every octave (a cylinder); the d = 6 edge — the tritones —
// is glued to itself a half-turn along (u ~ u+6), which is the twist that
// makes it a Möbius strip. The d = 0 edge (unisons) is the strip's boundary:
// voice leadings hitting it reflect, like a mirror.

import type { PitchClass } from './notes';

export interface DyadPoint {
  /** Bottom note of the ≤ tritone spelling. */
  u: PitchClass;
  /** Interval in semitones, 0..6. */
  d: number;
}

const mod12 = (x: number) => ((x % 12) + 12) % 12;

/** Reduce an unordered pair of (real-valued) pitches to strip coordinates. */
export function reduceDyad(a: number, b: number): DyadPoint {
  const up = mod12(b - a); // interval reading a as the bottom
  if (up <= 6) return { u: mod12(a), d: up };
  return { u: mod12(b), d: 12 - up };
}

/**
 * Sample a two-voice voice leading as a path on the strip. Voices move in real
 * pitch space (voice i glides from[i] → to[i]); each sample is re-reduced, so
 * reflections off the unison boundary and crossings of the tritone fold appear
 * exactly where the topology puts them.
 */
export function dyadPath(
  from: [number, number],
  to: [number, number],
  steps = 48,
): DyadPoint[] {
  const out: DyadPoint[] = [];
  for (let s = 0; s <= steps; s++) {
    const t = s / steps;
    out.push(reduceDyad(from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t));
  }
  return out;
}
